import { useState, type ReactElement } from 'react';
import { CHANNEL_RULES } from '@gr/core/distribution/channels/channel-types';
import type { ChannelDto } from '@gr/core/distribution/channels/dto';
import type { PostVariantDto } from '@gr/core/content/posts/dto';
import { useT } from '../../../../app/di';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormTextArea, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { Tabs } from '../../../../ui-kit/Tabs/Tabs';
import { ChannelTypeIcon } from '../../../distribution/channels';
import styles from './VariantsEditor.module.scss';

export interface VariantsEditorProps {
  channels: readonly ChannelDto[];
  variants: readonly PostVariantDto[];
  editable: boolean;
  onSave(channelId: string, body: string): Promise<ServerOutcome>;
}

/** POST-UC-4, POST-UC-4.CC-1: своя подпись для канала; пусто — используется основной текст. На вкладке — значок платформы. */
export function VariantsEditor({ channels, variants, editable, onSave }: VariantsEditorProps): ReactElement {
  const { t } = useT();
  const [active, setActive] = useState(channels[0]?.id ?? '');
  const channel = channels.find((c) => c.id === active) ?? channels[0];
  return (
    <div className="u-stack">
      <p className="u-muted u-small">{t('posts.variants.hint')}</p>
      <Tabs label={t('posts.variants.title')} items={channels.map((c) => ({
          key: c.id,
          label: (
            <span className={styles.tab}>
              <ChannelTypeIcon type={c.type} />
              {c.name}
            </span>
          ),
        }))} active={channel?.id ?? ''} onChange={setActive} />
      {channel ? (
        <VariantForm key={channel.id} channel={channel} body={variants.find((v) => v.channelId === channel.id)?.body ?? ''} editable={editable} onSave={(body) => onSave(channel.id, body)} />
      ) : null}
    </div>
  );
}

function VariantForm({ channel, body, editable, onSave }: { channel: ChannelDto; body: string; editable: boolean; onSave(body: string): Promise<ServerOutcome> }): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<{ body: string }>({ defaultValues: { body }, values: { body }, fields: ['body'], onSubmit: (values) => onSave(values.body) });
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FormTextArea
        control={form.control}
        name="body"
        label={channel.name}
        rows={6}
        placeholder={t('posts.variants.useMain')}
        maxCounter={CHANNEL_RULES[channel.type].captionLimit}
        disabled={!editable || form.formState.isSubmitting}
      />
      {editable ? (
        <div>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('posts.variants.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
